import { createAsyncThunk } from '@reduxjs/toolkit';
import { appInformation } from 'src/constants';
import { RootState } from 'src/redux/store';
import Api from 'src/services/ApiService';
import {
  CheckQuotationAvailabilityRequest,
  CheckQuotationAvailabilityResponse,
  CommonAxiosResponse,
  ComputeSRPRequest,
  ComputeSRPResponse,
  CreateTemplateResponse,
  GetQuotationCategoriesResponse,
  GetQuotationProductsRequest,
  GetQuotationProductsResponse,
  GetTemplateDetailsResponse,
  GetTemplateListResponse,
  GetTemplateRequest,
  NewTemplateDetails
} from 'src/types';

const isProduction =
  appInformation?.branch_cicd === 'master' &&
  appInformation?.env === 'production';

export const getQuotationsCategoriesThunk = createAsyncThunk<
  CommonAxiosResponse<GetQuotationCategoriesResponse>,
  undefined,
  { state: RootState }
>('quotationV2/getQuotationsCategoriesThunk', async (data) => {
  const response = await Api.get('/quotations/getCategories', data);
  return response;
});

export const getQuotationsProductsThunk = createAsyncThunk<
  CommonAxiosResponse<GetQuotationProductsResponse>,
  GetQuotationProductsRequest,
  { state: RootState }
>('quotationV2/getQuotationsProductsThunk', async (data) => {
  /**
   * Change the base URL for production environment to use the internal API.
   * This is necessary because this endpoint is heavy and specific to a one server ec2 instance
   */
  const isProduction =
    appInformation?.branch_cicd === 'master' &&
    appInformation?.env === 'production';

  const response = await Api.get(`/quotations/category/products/get`, data, {
    ...(isProduction && { baseURL: 'https://internal-api.pcworth.com/api' })
  });

  // if (!response.success) {
  //   throw new Error('Failed at getQuotationsProductsThunk.');
  // }
  return response;
});

export const checkQuotationAvailabilityThunk = createAsyncThunk<
  CommonAxiosResponse<CheckQuotationAvailabilityResponse>,
  CheckQuotationAvailabilityRequest,
  { state: RootState }
>('quotationV2/checkQuotationAvailabilityThunk', async (data) => {
  // const response = await Api.post(
  //   `/quotations/checkQuotationAvailability`,
  //   data
  // );

  const response = await Api.post(
    `/quotations/checkQuotationAvailability`,
    data,
    {
      ...(isProduction && { baseURL: 'https://internal-api.pcworth.com/api' })
    }
  );

  if (!response.success) {
    throw new Error('Failed at checkQuotationAvailabilityThunk.');
  }
  return response;
});

/**
 * Template Thunks
 */
export const getTemplateListThunk = createAsyncThunk<
  CommonAxiosResponse<GetTemplateListResponse>,
  GetTemplateRequest,
  { state: RootState }
>('quotationV2/getTemplateListThunk', async (data) => {
  const response = await Api.get('/quotation/get', data);

  if (!response.success) {
    throw new Error('Failed at getTemplateListThunk.');
  }
  return response;
});

export const getTemplateDetailsThunk = createAsyncThunk<
  CommonAxiosResponse<GetTemplateDetailsResponse>,
  GetTemplateRequest,
  { state: RootState }
>('quotationV2/getTemplateDetailsThunk', async (data) => {
  const response = await Api.get(`/quotation/get/${data.template_id}`, data);

  if (!response.success) {
    throw new Error('Failed at getTemplateListThunk.');
  }
  return response;
});

export const createTemplateThunk = createAsyncThunk<
  CommonAxiosResponse<CreateTemplateResponse>,
  NewTemplateDetails,
  { state: RootState }
>('quotationV2/createTemplateThunk', async (template) => {
  const response = await Api.post(`/quotation/create`, template);

  return response;
});

export const updateTemplateThunk = createAsyncThunk<
  CommonAxiosResponse<GetTemplateDetailsResponse>,
  NewTemplateDetails,
  { state: RootState }
>('quotationV2/updateTemplateDetailsThunk', async (template) => {
  const response = await Api.patch(
    `/quotation/update/${template.id}`,
    template
  );

  if (!response.originalData.success) {
    throw new Error('Failed at updateTemplateThunk.');
  }

  return response;
});

export const onDeleteTemplateThunk = createAsyncThunk<
  CommonAxiosResponse<GetTemplateDetailsResponse>,
  number | undefined,
  { state: RootState }
>('quotationV2/deleteTemplateDetailsThunk', async (template_id) => {
  const response = await Api.delete(`/quotation/delete/${template_id}`);

  if (!response.originalData.success) {
    throw new Error('Failed at deleteTemplateThunk.');
  }

  return response;
});

export const onComputeSrpThunk = createAsyncThunk<
  CommonAxiosResponse<ComputeSRPResponse>,
  ComputeSRPRequest,
  { state: RootState }
>('quotationV2/onComputeSrpThunk', async (data) => {
  const response = await Api.post('/quotations/srp_computation/get', data);

  // if (!response.originalData.success) {
  //   throw new Error('Failed at onComputeSrpThunk.');
  // }

  return response;
});
